import styled, { keyframes, css } from 'styled-components';
import { motion } from 'framer-motion';
import { media } from '../../utils';

// Floating action button
export const FAB = styled(motion.button)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.mainColor};
  background: ${({ theme }) => theme.bgColor};
  color: ${({ theme }) => theme.mainColor};
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(51, 255, 153, 0.3);
  transition: all 0.3s;

  &:hover {
    background: ${({ theme }) => theme.mainColor};
    color: ${({ theme }) => theme.bgColor};
    box-shadow: 0 6px 30px rgba(51, 255, 153, 0.5);
  }

  ${media('mobile')} {
    width: 50px;
    height: 50px;
    bottom: 20px;
    right: 20px;
    font-size: 1.2rem;
  }
`;

// Chat window
export const ChatWindow = styled(motion.div)`
  position: fixed;
  bottom: 100px;
  right: 30px;
  width: 400px;
  height: 550px;
  background: #0a0a2e;
  border: 1px solid rgba(51, 255, 153, 0.3);
  border-radius: 16px;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);

  ${media('mobile')} {
    width: calc(100vw - 20px);
    height: calc(100vh - 140px);
    right: 10px;
    bottom: 80px;
  }
`;

// Chat header
export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: linear-gradient(135deg, #000035, #1a1a4e);
  border-bottom: 1px solid rgba(51, 255, 153, 0.2);
`;

export const ChatHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;

  span.ai-icon {
    color: ${({ theme }) => theme.mainColor};
    font-size: 1.2rem;
  }
`;

export const ProviderSelect = styled.select`
  background: rgba(255, 255, 255, 0.1);
  color: #ccc;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 0.75rem;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.mainColor};
  }

  option {
    background: #0a0a2e;
    color: #fff;
  }
`;

export const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #aaa;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;

  &:hover {
    color: #fff;
  }
`;

// Messages area
export const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(51, 255, 153, 0.3);
    border-radius: 4px;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const MessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 0.9rem;
  line-height: 1.5;
  animation: ${fadeIn} 0.3s ease;
  white-space: pre-wrap;
  word-break: break-word;

  ${({ $isUser }) =>
    $isUser
      ? css`
          align-self: flex-end;
          background: linear-gradient(135deg, #33ff99, #28cc7a);
          color: #000;
          border-bottom-right-radius: 4px;
        `
      : css`
          align-self: flex-start;
          background: rgba(255, 255, 255, 0.08);
          color: #e0e0e0;
          border-bottom-left-radius: 4px;
        `}
`;

const pulse = keyframes`
  0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
`;

export const TypingIndicator = styled.div`
  align-self: flex-start;
  display: flex;
  gap: 5px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  border-bottom-left-radius: 4px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.mainColor};
    animation: ${pulse} 1.4s infinite;

    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

// Input area
export const InputArea = styled.form`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid rgba(51, 255, 153, 0.15);
  background: rgba(0, 0, 30, 0.8);
`;

export const ChatInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${({ theme }) => theme.mainColor};
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
  }
`;

export const SendButton = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  background: ${({ theme }) => theme.mainColor};
  color: ${({ theme }) => theme.bgColor};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.85;
    transform: scale(1.02);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// Quick action buttons
export const QuickActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 16px;
  border-top: 1px solid rgba(51, 255, 153, 0.1);
`;

export const QuickActionBtn = styled.button`
  padding: 6px 12px;
  border: 1px solid rgba(51, 255, 153, 0.3);
  border-radius: 20px;
  background: transparent;
  color: ${({ theme }) => theme.mainColor};
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: rgba(51, 255, 153, 0.15);
  }
`;
